import classNames from "classnames";
import { ReactNode, RefObject } from "react";
import { useModalControl } from "../../customHooks/useModalControl";
import { useWindowDimensions } from "../../customHooks/useWindowDimensions";
import { Icon } from "../icons/icon";
import { PopoverMenu } from "../inputs/popover";
import { ModalComponent } from "../modals";

export type Option = {
  icon?: string;
  text: string;
  onClick: () => void;
  hidden?: boolean;
  count?: number;
};

const Options = ({
  options,
  optionsClassName,
}: {
  options: Option[];
  optionsClassName?: string;
}) => {
  return (
    <div className="flex w-full flex-col">
      {options
        .filter((i) => !i.hidden)
        .map((opt, idx) => {
          return (
            <button
              key={`option-${idx}`}
              type="button"
              className={classNames(
                "w-full text-left flex flex-row gap-2 items-center text-sm font-medium py-3 border-b last:border-b-0 px-4 md:hover:bg-light-green-bg-50",
                optionsClassName
              )}
              onClick={(e) => {
                e.stopPropagation();
                opt.onClick();
              }}
            >
              {opt.icon ? (
                <Icon svg={opt.icon} width={20} height={20} />
              ) : undefined}
              <p className="text-left flex-1">{opt.text}</p>
              {opt?.count ? (
                <p
                  className={classNames(
                    `
                    transition-all duration-500
                    text-xs px-1 min-h-[20px] min-w-[20px] rounded-full
                    flex items-center justify-center
                    !bg-pry-green-200 !text-white !font-medium`
                  )}
                >
                  {opt.count}
                </p>
              ) : undefined}
            </button>
          );
        })}
    </div>
  );
};

export const MorePopOverMenu = ({
  options = [],
  className = "",
  ref,
  children,
}: {
  options?: Option[];
  className?: string;
  ref?: RefObject<HTMLButtonElement | null>;
  children?: ReactNode;
}) => {
  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const { width } = useWindowDimensions();

  return (
    <>
      <PopoverMenu
        className={`${className}`}
        onPopoverClick={(e) => {
          e.stopPropagation();

          if (width < 768) {
            e.preventDefault();
            handleModalOpen("popOverMenuModal");
          }
        }}
        customMenuItems={({ close }) => {
          return (
            <Options
              options={options.map((o) => ({
                ...o,
                onClick: () => {
                  o.onClick();
                  close();
                  handleModalClose();
                },
              }))}
            />
          );
        }}
        position="bottom end"
        menuClassName="bg-white border border-pry-dark-green-300/[15%] min-w-[196px] rounded-[8px]"
      >
        {children ? (
          children
        ) : (
          <button
            className=""
            type="button"
            ref={ref}
            disabled={(options?.filter((o) => !o.hidden)?.length ?? 0) === 0}
          >
            <Icon svg={"ellipses"} width={18} height={4} />
          </button>
        )}
      </PopoverMenu>

      <ModalComponent
        isModalOpen={modalOpen && modal === "popOverMenuModal"}
        onModalClose={() => {
          handleModalClose();
        }}
        hasButtons={false}
        showMobileTopBar
        showHeader={false}
        closeOnOutsideClick
        isMenuModal={false}
        modalClassName="!px-0"
      >
        <Options
          options={options.map((o) => ({
            ...o,
            onClick: () => {
              o.onClick();
              handleModalClose();
            },
          }))}
        />
      </ModalComponent>
    </>
  );
};
